import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import breakDefault from '../../../assets/imgs/articles/detail.png';
import { useAuthContext } from '../../../context/AuthContextProvider';

const plansFormatter = new Intl.ListFormat('pt-BR', {
  style: 'long',
  type: 'conjunction',
});

const MAX_MARKET_CHIPS = 3;

const chipLabelSx = {
  maxWidth: 104,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const getGridTemplateColumns = ({
  compact,
  featured,
  hasFullFeaturedLayout,
}) => {
  if (compact) {
    return { xs: '1fr', md: '1fr', lg: '1fr' };
  }

  if (hasFullFeaturedLayout) {
    return {
      xs: '1fr',
      md: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
      lg: 'minmax(0, 1.25fr) minmax(0, 0.75fr)',
    };
  }

  if (featured) {
    return {
      xs: '1fr',
      md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
      lg: 'minmax(0, 1.08fr) minmax(0, 0.92fr)',
    };
  }

  return {
    xs: '1fr',
    md: '280px minmax(0, 1fr)',
    lg: '320px minmax(0, 1fr)',
  };
};

const getMediaMinHeight = ({ featured, hasFullFeaturedLayout }) => {
  if (!featured) return { xs: 220, md: 230 };
  if (hasFullFeaturedLayout) return { xs: 250, md: 410 };
  return { xs: 235, md: 410 };
};

const getContentMinHeight = ({ compact, compactMinHeight, featured }) => {
  if (compact) return compactMinHeight;
  if (featured) return { md: 410 };
  return { md: 230 };
};

const getTitleLineClamp = ({
  compact,
  featured,
  hasDenseCompactLayout,
  hasTallCompactLayout,
}) => {
  if (compact && hasDenseCompactLayout) return 2;
  if (compact) return hasTallCompactLayout ? 3 : 2;
  if (featured) return 4;
  return 3;
};

export default function SingleArticle({
  id,
  name,
  markets,
  publishedAt,
  slug,
  headerImage,
  plans = [],
  featured = false,
  compact = false,
  compactSize = 'default',
  compactDensity = 'default',
  featuredLayout = 'default',
  summary,
}) {
  const { auth } = useAuthContext();
  const userPlan = auth?.user?.subscription?.plan;
  const href = `/artigo/${slug}/${id}`;
  const loginHref = `/login?redirectUrl=${encodeURIComponent(href)}`;
  const hasPlanRestriction = plans?.length > 0;
  const hasRequiredPlan =
    auth?.isLoggedIn &&
    hasPlanRestriction &&
    plans?.some((plan) => plan === userPlan?.name);
  const hasRestrictedPlan =
    auth?.isLoggedIn && hasPlanRestriction && !hasRequiredPlan;
  const shouldShowSubscriberLock = !auth?.isLoggedIn;
  const shouldShowGrantedLock = hasRequiredPlan;
  const shouldShowLock =
    shouldShowSubscriberLock || shouldShowGrantedLock || hasRestrictedPlan;
  const AccessIcon = shouldShowGrantedLock
    ? LockOpenOutlinedIcon
    : LockOutlinedIcon;
  const hasFullFeaturedLayout = featured && featuredLayout === 'fullWidth';
  const hasTallCompactLayout = compact && compactSize === 'tall';
  const hasDenseCompactLayout = compact && compactDensity === 'dense';
  const compactMinHeight = hasDenseCompactLayout
    ? { xs: 124, md: 0 }
    : hasTallCompactLayout
      ? { xs: 136, md: 168 }
      : 132;
  const gridTemplateColumns = getGridTemplateColumns({
    compact,
    featured,
    hasFullFeaturedLayout,
  });
  const mediaMinHeight = getMediaMinHeight({
    featured,
    hasFullFeaturedLayout,
  });
  const contentMinHeight = getContentMinHeight({
    compact,
    compactMinHeight,
    featured,
  });
  const titleLineClamp = getTitleLineClamp({
    compact,
    featured,
    hasDenseCompactLayout,
    hasTallCompactLayout,
  });
  const visibleMarkets = markets?.slice(0, MAX_MARKET_CHIPS) ?? [];
  const hiddenMarketsCount = Math.max(
    (markets?.length ?? 0) - MAX_MARKET_CHIPS,
    0,
  );

  const lockAriaLabel = shouldShowSubscriberLock
    ? 'Artigo disponível somente para assinantes. Faça login para acessar.'
    : shouldShowGrantedLock
      ? 'Artigo disponível pelo seu plano de assinatura'
      : 'Artigo com acesso restrito por plano';

  const lockTooltipTitle = shouldShowSubscriberLock ? (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Conteúdo somente para assinantes.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Faça login para acessar este artigo.
      </Typography>
      <Typography
        component={RouterLink}
        to={loginHref}
        variant="body2"
        onClick={(event) => event.stopPropagation()}
        sx={{
          color: 'common.white',
          display: 'inline-block',
          fontWeight: 700,
          mt: 0.75,
          textDecoration: 'underline',
        }}>
        Entrar na plataforma
      </Typography>
    </Box>
  ) : shouldShowGrantedLock ? (
    'Você tem acesso exclusivo a este artigo por ser assinante da plataforma.'
  ) : (
    `Este artigo é exclusivo para membros do(s) plano(s) ${plansFormatter.format(
      plans?.map((plan) => plan),
    )}`
  );

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        overflow: 'hidden',
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'common.white',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 18px 44px rgba(0, 0, 0, 0.10)',
        },
      }}>
      <CardActionArea
        component={RouterLink}
        to={href}
        sx={{
          height: compact ? { xs: 'auto', md: '100%' } : '100%',
          minHeight: compact ? compactMinHeight : undefined,
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'stretch',
        }}>
        {!compact && (
          <Box
            sx={{
              position: 'relative',
              minHeight: mediaMinHeight,
              backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.30)), url(${
                headerImage?.url ?? breakDefault
              })`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }}>
            {featured && (
              <Chip
                label="Destaque"
                color="primary"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 18,
                  left: 18,
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
        )}

        <Stack
          spacing={
            hasDenseCompactLayout ? 0.5 : compact ? 1 : featured ? 2.5 : 1.5
          }
          sx={{
            position: 'relative',
            justifyContent: 'space-between',
            p: {
              xs: compact ? 1.75 : 2.5,
              md: hasDenseCompactLayout ? 1.25 : compact ? 2 : featured ? 4 : 3,
              lg: hasDenseCompactLayout
                ? 1.25
                : compact
                  ? 2.25
                  : featured
                    ? 4
                    : 3,
            },
            minHeight: contentMinHeight,
          }}>
          {shouldShowLock && (
            <Tooltip arrow title={lockTooltipTitle}>
              <AccessIcon
                aria-label={lockAriaLabel}
                fontSize={compact ? 'small' : 'medium'}
                sx={{
                  position: 'absolute',
                  top: hasDenseCompactLayout ? 8 : compact ? 12 : 20,
                  right: hasDenseCompactLayout ? 8 : compact ? 12 : 20,
                  color: shouldShowGrantedLock
                    ? 'rgba(46, 125, 50, 0.72)'
                    : 'grey.600',
                }}
              />
            </Tooltip>
          )}

          <Stack
            spacing={hasDenseCompactLayout ? 0.5 : compact ? 1 : 1.5}
            pr={shouldShowLock ? (compact ? 3.5 : 4) : 0}
            minWidth={0}>
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="nowrap"
              useFlexGap
              sx={{ minWidth: 0, overflow: 'hidden' }}>
              {visibleMarkets.map((market) => (
                <Chip
                  key={market}
                  label={market}
                  size="small"
                  sx={{
                    height: hasDenseCompactLayout ? 20 : compact ? 22 : 26,
                    minWidth: 0,
                    maxWidth: hasDenseCompactLayout ? 88 : compact ? 104 : 140,
                    fontWeight: 700,
                    fontSize: compact ? '0.65rem' : undefined,
                    color: 'primary.dark',
                    backgroundColor: 'rgba(255, 130, 17, 0.12)',
                    '& .MuiChip-label': chipLabelSx,
                  }}
                />
              ))}
              {hiddenMarketsCount > 0 && (
                <Chip
                  label={`+${hiddenMarketsCount}`}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    height: hasDenseCompactLayout ? 20 : compact ? 22 : 26,
                    fontWeight: 800,
                    fontSize: compact ? '0.65rem' : undefined,
                    color: 'primary.dark',
                    backgroundColor: 'rgba(255, 130, 17, 0.12)',
                  }}
                />
              )}
            </Stack>

            <Typography
              component="h2"
              variant={
                featured
                  ? 'h5'
                  : hasDenseCompactLayout
                    ? 'body2'
                    : compact
                      ? 'subtitle1'
                      : 'h6'
              }
              sx={{
                color: 'secondary.main',
                fontWeight: 800,
                lineHeight: hasDenseCompactLayout
                  ? 1.16
                  : compact
                    ? 1.18
                    : 1.14,
                display: '-webkit-box',
                WebkitLineClamp: titleLineClamp,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
              {name}
            </Typography>

            {summary && !compact && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: featured ? 3 : 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                dangerouslySetInnerHTML={{ __html: summary }}
              />
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexShrink: 0 }}>
              Publicado em {publishedAt}
            </Typography>
          </Stack>

          <Box display={compact ? 'none' : 'block'}>
            <Button
              component="span"
              variant={featured ? 'contained' : 'text'}
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              size={compact ? 'small' : 'medium'}
              sx={{
                alignSelf: 'flex-start',
                fontWeight: 800,
                px: featured ? 2.5 : 0,
                '&:hover': {
                  px: featured ? 2.5 : 0,
                  backgroundColor: featured ? 'primary.dark' : 'transparent',
                },
              }}>
              Ler artigo
            </Button>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
